import { useState } from 'react';
import { useQuery } from 'react-query';

import { DEFAULT_ERROR_MESSAGE } from '@/config/text.const';
import { useEffectExceptOnMount } from '@/hooks/useEffectExceptOnMount.hook';
import { IMPORTING_TOOL_UPLOADED_FILES_PER_PAGE } from '@/config/common.config';
import { notificationService } from '@/services/notification/notification.client';
import { importingToolHttpClient } from '@/services/http/clients/importingToolHttpClient';
import { ImportingToolDocument, ImportingToolDocumentWithUserName, ImportingToolPaginatedList }
  from '../models/getPaginatedDocumentsResult.interface';
import { useSearchUsers } from '@/hooks/identity/useSearchUsers';

type PageConfig = {
  readonly pageIndex: number;
  readonly pageSize: number;
};

export function useGetImportingToolFiles(
  onSuccess?: (data: ImportingToolPaginatedList<ImportingToolDocumentWithUserName>) => void) {
  const [pageConfig, setPageConfig] = useState<PageConfig>({
    pageIndex: 1,
    pageSize: IMPORTING_TOOL_UPLOADED_FILES_PER_PAGE
  });

  const { searchUsers } = useSearchUsers();

  const loadData = async (): Promise<ImportingToolPaginatedList<ImportingToolDocumentWithUserName>> => {
    const requestUrl = '/paginated-documents';
    const response = await importingToolHttpClient.get<ImportingToolPaginatedList<ImportingToolDocument>>(requestUrl, {
      responseType: 'json',
      params: {
        pageIndex: pageConfig.pageIndex,
        pageSize: pageConfig.pageSize
      },
    });

    const uniqueUserIds = new Set(
      response.data?.items.filter(document => !!document.uploadedBy).map(document => {
        return document.uploadedBy!;
      })
    );

    const users = await searchUsers({ ids: Array.from(uniqueUserIds) });

    const getUserName = (userId: UUID | null) => {
      const uploadedByUser = users?.find(user => user?.Id == userId);
      return uploadedByUser ? `${uploadedByUser.FirstName} ${uploadedByUser.LastName}` : 'Unknown User';
    };

    const importingToolDocumentsWithUserName: ImportingToolPaginatedList<ImportingToolDocumentWithUserName> = {
      pageIndex: response.data.pageIndex,
      totalPages: response.data.totalPages,
      totalCount: response.data.totalCount,
      hasPreviousPage: response.data.hasPreviousPage,
      hasNextPage: response.data.hasNextPage,
      items: response.data.items.map((document): ImportingToolDocumentWithUserName => {
        return {
          ...document,
          uploadedByUserName: getUserName(document.uploadedBy)
        };
      })
    };

    return importingToolDocumentsWithUserName;
  };

  const { refetch, isFetching, data } = useQuery<ImportingToolPaginatedList<ImportingToolDocumentWithUserName>>({
    queryKey: 'uploaded-importing-files',
    queryFn: loadData,
    enabled: false,
    retry: false,
    onError: (err: any) => notificationService.open({
      title: 'Error',
      text: err?.message ?? DEFAULT_ERROR_MESSAGE,
      type: 'danger'
    }),
    onSuccess: (response) => {
      onSuccess?.(response);
    }
  });

  const getUploadedFiles = (pageIndex: number, pageSize: number) => {
    setPageConfig({ pageIndex, pageSize });
  };

  useEffectExceptOnMount(() => {
    refetch();
  }, [pageConfig]);

  return {
    getUploadedFiles,
    isLoading: isFetching,
    files: data?.items ?? [],
    totalPages: data?.totalPages ?? 0,
  } as const;
}
